'use client';

import { useTranslations } from 'next-intl';
import React from 'react';
import styles from './UhxSection.module.css';
import { CheckOverCircle, InfoIcon } from 'assets/icons';
import { UhxShopifyMap } from 'content/products/uhx';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { Currency } from 'utils/Currency';
import { useFormatPrice, CurrencySymbol } from 'hooks/pricing/useFormattedPrice';
import { productMap_home } from 'content/products/uhHome';

// Base Section Container
const SectionContainer = React.forwardRef<
  HTMLDivElement,
  { show: boolean } & React.HTMLAttributes<HTMLDivElement>
>(({ show, className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={
      className
        ? `${styles.sectionContainer} ${className}`
        : styles.sectionContainer
    }
    data-show={show ? 'true' : 'false'}
    {...props}
  >
    {children}
  </div>
));

SectionContainer.displayName = 'SectionContainer';

// Section Title Row
const SectionTitleRow = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => (
  <div
    className={
      className
        ? `${styles.sectionTitleRow} ${className}`
        : styles.sectionTitleRow
    }
  >
    {children}
  </div>
);

// UltrahumanX Content Container
const UhxContentContainer = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.uhxContentContainer}>{children}</div>
);

// Large Prompt Button Content
const LargePromptButton = ({
  className,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    className={
      className
        ? `${styles.largePromptButton} ${className}`
        : styles.largePromptButton
    }
    {...props}
  >
    {children}
  </button>
);

// No UltrahumanX Button
const NoUhxButton = ({
  className,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    className={className ? `${styles.noUhxButton} ${className}` : styles.noUhxButton}
    {...props}
  >
    {children}
  </button>
);

// UHX Card for Subscribers
const UhxCard = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => (
  <div className={className ? `${styles.uhxCard} ${className}` : styles.uhxCard}>
    {children}
  </div>
);

interface UltrahumanXSectionProps {
  uhx: boolean;
  uhxCart: 'Year1' | 'Year2' | false | null;
  setUhxCart: (value: 'Year1' | 'Year2' | false | null) => void;
  store: ShopifyStore;
  defaultCurrency: Currency;
  ref: React.RefObject<HTMLDivElement> | undefined;
  openUhxInfoModal: () => void;
}

const UltrahumanXSection = React.forwardRef<
  HTMLDivElement,
  UltrahumanXSectionProps
>(
  (
    { uhx, uhxCart, setUhxCart, store, defaultCurrency, openUhxInfoModal },
    ref,
  ) => {
    const t = useTranslations('ring');
    const formatPriceWithComma = useFormatPrice();
    // Get currency symbol safely (same as Ring buy section)
    const currencySymbol =
      CurrencySymbol[defaultCurrency.toLowerCase() as Currency];
    if (uhx) {
      return (
        <SectionContainer ref={ref} show={true} className="ultrahumanx-section">
          <UhxContentContainer>
            <UhxCard className="card ultrahumanx">
              <div className="uhx-content">
                <div className="content">
                  <h5>UltrahumanX subscription applied</h5>
                </div>
              </div>
            </UhxCard>
          </UhxContentContainer>
        </SectionContainer>
      );
    }

    return (
      <SectionContainer ref={ref} show={true} className="ultrahumanx-section">
        <SectionTitleRow className="section-row-title">
          <h5>
            {t('ring.productSelection.uhxSectionNew.title')}{' '}
            <span className="subtitle">Protect your new Ultrahuman Home.</span>
          </h5>
          <button
            onClick={openUhxInfoModal}
            aria-label="Open UltrahumanX Coverage information modal"
          >
            <InfoIcon fill="#000000" />
          </button>
        </SectionTitleRow>

        <UhxContentContainer>
          <LargePromptButton
            className={`ultrahumanx gtm ${uhxCart === 'Year1' ? 'active' : ''}`}
            data-buttontype="uhx selection 1 year"
            data-product="Ultrahuman Home"
            onClick={() => setUhxCart('Year1')}
            disabled={!productMap_home?.variantId[store]}
          >
            <div className="content-wrapper">
              <div className="content">
                <div className="title-bar">
                  <div className="title">
                    <span>
                      <strong>
                        {t('ring.productSelection.uhxSectionNew.optionA.head')}
                      </strong>
                    </span>
                  </div>
                  <div className="disclaimer">
                    <div className="price">
                      <>
                        {currencySymbol}
                        {formatPriceWithComma(UhxShopifyMap[store].Year1.price)}
                      </>
                    </div>
                  </div>
                </div>

                <hr className="seperator-bar" />

                <div className="list">
                  <p>
                    <CheckOverCircle className="checkmark" />
                    Accidental Damage Protection
                  </p>
                  <p>
                    <CheckOverCircle className="checkmark" />
                    Priority Care
                  </p>
                </div>
              </div>
            </div>
          </LargePromptButton>

          <LargePromptButton
            className={`ultrahumanx gtm ${
              uhxCart === 'Year2' ? 'active' : ''
            } paddingTop`}
            data-buttontype="uhx selection 2 year"
            data-product="Ultrahuman Home"
            onClick={() => setUhxCart('Year2')}
            disabled={!productMap_home?.variantId[store]}
          >
            <div className="label">
              {t('ring.productSelection.uhxSectionNew.popular')}
            </div>

            <div className="content-wrapper">
              <div className="content">
                <div className="title-bar">
                  <div className="title">
                    <span>
                      <strong>
                        {t('ring.productSelection.uhxSectionNew.optionB.head')}
                      </strong>
                    </span>
                    <span className="save-tag">
                      {t('ring.SAVE')}{' '}
                      {Math.floor(
                        ((UhxShopifyMap[store].Year1.price -
                          UhxShopifyMap[store].Year2.price / 2) /
                          UhxShopifyMap[store].Year1.price) *
                          100,
                      )}
                      %
                    </span>
                  </div>

                  <div className="disclaimer">
                    <div className="price">
                      <>
                        {currencySymbol}
                        {formatPriceWithComma(UhxShopifyMap[store].Year2.price)}
                      </>
                    </div>
                    <div className="disclaimer-text">
                      <span>
                        ({t('ring.THATS_JUST')}{' '}
                        <>
                          {currencySymbol}
                          {formatPriceWithComma(
                            UhxShopifyMap[store].Year2.price / 2,
                          )}
                        </>
                        /year)
                      </span>
                    </div>
                  </div>
                </div>

                <hr className="seperator-bar" />

                <div className="list">
                  <p>
                    <CheckOverCircle className="checkmark" />
                    Includes all 1 Year Coverage benefits
                  </p>
                  <p>
                    <CheckOverCircle className="checkmark" />
                    Environmental Wear Protection
                  </p>
                  <p>
                    <CheckOverCircle className="checkmark" />
                    Ultrahuman Beta Access
                  </p>
                </div>
              </div>
            </div>
          </LargePromptButton>

          <NoUhxButton
            className={`no-ultrahumanx gtm ${
              uhxCart === false ? 'active' : ''
            }`}
            data-buttontype="uhx selection"
            data-product="Ultrahuman Home"
            onClick={() => setUhxCart(false)}
            disabled={!productMap_home?.variantId[store]}
          >
            <span>
              <strong>
                I don&apos;t want to protect my new Ultrahuman Home.
              </strong>
            </span>
          </NoUhxButton>

          {/* <div className="footer-info">
          {t('ring.productSelection.uhxSectionNew.tnc')}
        </div> */}
        </UhxContentContainer>
      </SectionContainer>
    );
  },
);

UltrahumanXSection.displayName = 'UltrahumanXSection';

export default React.memo(UltrahumanXSection);
